import React, { useState } from 'react';
import { colors, typography, spacing } from '../styles/design-system';
import { SportTable } from '../utils/renderSportTable';

interface SoccerDataDisplayProps {
  data: any;
  homeTeam: string;
  awayTeam: string;
}

const SoccerDataDisplay: React.FC<SoccerDataDisplayProps> = ({ data, homeTeam, awayTeam }) => {
  const [selectedTeam, setSelectedTeam] = useState<'home' | 'away'>('home');

  // 수신 데이터 디버그 로그
  React.useEffect(() => {
    console.log('SoccerDataDisplay 수신 데이터:', data);
    console.log('데이터 키:', Object.keys(data || {}));
    console.log('상대전적:', data?.head_to_head);
    console.log('팀 통계:', data?.team_stats);
    console.log('시즌 기록:', data?.season_records);
  }, [data]);

  // 데이터 구조에서 추출
  const headToHead = data.head_to_head || {};
  const teamStats = data.team_stats || {};
  const seasonRecords = data.season_records || {};
  const matchInfo = data.match_info || {};

  // 2D 배열을 적절히 처리하여 테이블 렌더링
  // 공통 테이블 렌더는 utils/renderSportTable.SportTable로 위임
  const renderTable = (tableData: any, title?: string): React.ReactElement | null => (
    <SportTable tableData={tableData} title={title} styles={styles} />
  );

  // Get team-specific data with backend classification
  const getTeamSpecificData = () => {
    const team = selectedTeam === 'home' ? homeTeam : awayTeam;
    const result: any = {
      teamName: team,
      currentSeason: [],
      threeSeasonStats: [],
      recentMatches: null,
      headToHead: [],
      headToHeadStats: []
    };

    // Current season records
    if (seasonRecords.current && Array.isArray(seasonRecords.current)) {
      result.currentSeason = seasonRecords.current.filter((record: any) => {
        // Use backend classification if available
        if (record.team_type !== undefined) {
          return record.team_type === selectedTeam || record.team_type === null;
        }
        // Fallback to text-based classification
        const desc = record.description || '';
        return selectedTeam === 'home' ? 
          desc.includes('[홈팀]') || desc.includes(homeTeam) : 
          desc.includes('[원정팀]') || desc.includes(awayTeam);
      });
    }

    // Three season statistics
    if (teamStats.three_season && Array.isArray(teamStats.three_season)) {
      result.threeSeasonStats = teamStats.three_season.filter((record: any) => {
        // Use backend classification if available
        if (record.team_type !== undefined) {
          return record.team_type === selectedTeam || record.team_type === null;
        }
        // Fallback
        const desc = record.description || '';
        return selectedTeam === 'home' ? 
          desc.includes('[홈팀]') || desc.includes(homeTeam) : 
          desc.includes('[원정팀]') || desc.includes(awayTeam);
      });
    }

    // Recent matches
    const recentKey = selectedTeam === 'home' ? 'home_recent' : 'away_recent';
    if (teamStats[recentKey]) {
      result.recentMatches = teamStats[recentKey];
    }

    // Head to head records
    if (headToHead.records && Array.isArray(headToHead.records)) {
      result.headToHead = headToHead.records.filter((record: any) => {
        // Use backend classification if available
        if (record.team_type !== undefined && record.record_type !== undefined) {
          if (record.record_type === 'versus') {
            return record.team_type === selectedTeam || record.team_type === null;
          }
          return record.team_type === null;
        }
        
        // Fallback
        const desc = record.description || '';
        const isAgainstRecord = desc.includes('과의') || desc.includes('상대전적');
        
        if (isAgainstRecord) {
          if (selectedTeam === 'home') {
            return desc.includes('[원정팀]과의') || desc.includes(`${awayTeam}과의`);
          } else {
            return desc.includes('[홈팀]과의') || desc.includes(`${homeTeam}과의`);
          }
        }
        
        return !desc.includes('[홈팀]') && !desc.includes('[원정팀]') && 
               !desc.includes(homeTeam) && !desc.includes(awayTeam);
      });
    }
    
    // Head to head stats
    if (headToHead.stats && Array.isArray(headToHead.stats)) {
      result.headToHeadStats = headToHead.stats.filter((stat: any) => {
        // Use backend classification if available
        if (stat.team_type !== undefined) {
          return stat.team_type === selectedTeam || stat.team_type === null;
        }
        
        // Fallback
        const desc = stat.description || '';
        return selectedTeam === 'home' ? 
          (desc.includes('[홈팀]') || desc.includes(homeTeam)) : 
          (desc.includes('[원정팀]') || desc.includes(awayTeam));
      });
    }

    return result;
  };

  const teamData = getTeamSpecificData();

  return (
    <div style={styles.container}>
      {/* Match Info Header */}
      {matchInfo.date_time && (
        <div style={styles.matchInfoHeader}>
          <h3 style={styles.matchTitle}>
            {homeTeam} vs {awayTeam}
          </h3>
          <div style={styles.matchDetails}>
            <span>{matchInfo.date_time}</span>
            {matchInfo.venue && <span> | {matchInfo.venue}</span>}
          </div>
        </div>
      )}

      {/* Team Selection Buttons */}
      <div style={styles.teamButtons}>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'home' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('home')}
        >
          홈팀: {homeTeam}
        </button>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'away' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('away')}
        >
          원정팀: {awayTeam}
        </button>
      </div>

      {/* Team Statistics Content */}
      <div style={styles.statsContent}>
        <h3 style={styles.teamNameTitle}>
          {teamData.teamName} 상세 데이터
        </h3>

        {/* Current Season Statistics */}
        {teamData.currentSeason && teamData.currentSeason.length > 0 && (
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>현재 시즌 전적</h4>
            {teamData.currentSeason.map((record: any, idx: number) => (
              <div key={idx}>
                {renderTable(record)}
              </div>
            ))}
          </div>
        )}

        {/* Three Season Statistics */}
        {teamData.threeSeasonStats && teamData.threeSeasonStats.length > 0 && (
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>최근 3시즌 통계</h4>
            {teamData.threeSeasonStats.map((stat: any, idx: number) => (
              <div key={idx}>
                {renderTable(stat)}
              </div>
            ))}
          </div>
        )}

        {/* Recent Matches */}
        {teamData.recentMatches && (
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>최근 경기 전적</h4>
            {renderTable(teamData.recentMatches)}
          </div>
        )}

        {/* Head to Head */}
        {teamData.headToHead && teamData.headToHead.length > 0 && (
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>상대전적</h4>
            {teamData.headToHead.map((record: any, idx: number) => {
              // Skip if this is actually a stats record (check description)
              const desc = record.description || '';
              if (desc.includes('통계') || desc.includes('결과 종합')) {
                return null;
              }
              return (
                <div key={idx}>
                  {renderTable(record)}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Head to Head Stats */}
        {teamData.headToHeadStats && teamData.headToHeadStats.length > 0 && (
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>상대전적 통계</h4>
            {teamData.headToHeadStats.map((stat: any, idx: number) => {
              const table = renderTable(stat);
              // Only render if we got a valid table
              return table ? <div key={idx}>{table}</div> : null;
            })}
          </div>
        )}

        {/* No Data Message */}
        {teamData.currentSeason.length === 0 && 
         teamData.threeSeasonStats.length === 0 && 
         !teamData.recentMatches && 
         teamData.headToHead.length === 0 && 
         teamData.headToHeadStats.length === 0 && (
          <div style={styles.noData}>
            <p>{teamData.teamName}의 상세 데이터가 없음.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxHeight: '60vh',
    padding: spacing[6],
    backgroundColor: colors.white,
    overflow: 'auto' as const,
  },
  matchInfoHeader: {
    marginBottom: spacing[7],
    paddingBottom: spacing[5],
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  matchTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginBottom: spacing[3],
  },
  matchDetails: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
  },
  teamButtons: {
    display: 'flex',
    marginBottom: spacing[7],
  },
  teamButton: {
    flex: 1,
    padding: spacing[4],
    backgroundColor: colors.white,
    color: colors.gray[600],
    border: `1px solid ${colors.gray[300]}`,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  selectedTeamButton: {
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
  },
  statsContent: {
    minHeight: '400px',
    marginBottom: spacing[8],
  },
  teamNameTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[7],
    color: colors.black,
    paddingLeft: spacing[3],
    borderLeft: `3px solid ${colors.black}`,
  },
  section: {
    marginBottom: spacing[9],
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[6],
    color: colors.gray[800],
    borderBottom: `1px solid ${colors.gray[300]}`,
    paddingBottom: spacing[3],
  },
  tableContainer: {
    marginBottom: spacing[6],
  },
  tableTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing[3],
    color: colors.gray[600],
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    border: `1px solid ${colors.gray[200]}`,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: typography.fontSize.base,
  },
  th: {
    padding: spacing[4],
    backgroundColor: colors.gray[100],
    borderBottom: `1px solid ${colors.gray[300]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    fontWeight: typography.fontWeight.semibold,
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[800],
  },
  td: {
    padding: `${spacing[3]} ${spacing[4]}`,
    borderBottom: `1px solid ${colors.gray[200]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[700],
  },
  noData: {
    textAlign: 'center' as const,
    padding: spacing[10],
    color: colors.gray[500],
    fontSize: typography.fontSize.md,
    backgroundColor: colors.gray[50],
  },
};

export default SoccerDataDisplay;